import { useState, useEffect } from 'react';
import {
  TrendingUp, DollarSign, Target, Briefcase,
  Zap, Clock, CheckCircle2, Activity, Layers
} from 'lucide-react';
import {
  ResponsiveContainer,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  BarChart, Bar,
  PieChart, Pie, Cell,
  Legend,
  ComposedChart,
  Area
} from 'recharts';
import { useStore } from '../../store/useStore';
import { MetricCard } from '../MetricCard';
import { formatCurrency, formatCompactNumber } from '../../lib/format';
import { format, subDays, eachDayOfInterval, isSameDay, startOfDay } from 'date-fns';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b', '#10b981'];

export function TeamAnalyticsTab() {
  const { team, leads } = useStore();
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [timeRange]);

  // ── Data Filtering Logic ──────────────────────────────────────────────────
  const now = new Date();
  const getStartDate = () => {
    switch (timeRange) {
      case '7d': return subDays(now, 6);
      case '30d': return subDays(now, 29);
      case '90d': return subDays(now, 89);
      case 'all': 
        if (!leads || leads.length === 0) return subDays(now, 30);
        const earliest = Math.min(...leads.map(l => new Date(l.createdAt).getTime()));
        return isFinite(earliest) ? new Date(earliest) : subDays(now, 30);
    }
  };

  const startDate = getStartDate();
  
  // Leads ACTIVE in window: created before end, and (not closed or closed after start)
  const leadsInWindow = (leads || []).filter(l => {
    if (timeRange === 'all') return true;
    const createdDate = new Date(l.createdAt);
    if (createdDate > now) return false;
    
    // Check if it was closed before this window started
    const closeEntry = l.statusHistory?.find((h: any) => h.toStatus === 'closed-won' || h.toStatus === 'closed-lost');
    const closeDate = closeEntry ? new Date(closeEntry.timestamp) : (l.status.startsWith('closed') ? new Date(l.updatedAt) : null);
    
    if (closeDate && closeDate < startDate) return false;
    return true;
  });
  
  // 2. Deals WON in window (by status change or updatedAt)
  const getDealValue = (l: any) => {
    const offer = Number(l.offerAmount);
    const est = Number(l.estimatedValue);
    if (!isNaN(offer) && offer > 0) return offer;
    if (!isNaN(est) && est > 0) return est;
    return 0;
  };

  const wasWonInPeriod = (l: any, start: Date, end: Date) => {
    if (l.status !== 'closed-won') return false;
    const winEntry = l.statusHistory?.find((h: any) => h.toStatus === 'closed-won');
    const winDate = winEntry ? new Date(winEntry.timestamp) : new Date(l.updatedAt);
    return winDate >= start && winDate <= end;
  };

  const wonInWindow = (leads || []).filter(l => {
    if (timeRange === 'all') return l.status === 'closed-won';
    return wasWonInPeriod(l, startDate, now);
  });

  const totalRevenue = wonInWindow.reduce((sum, l) => sum + getDealValue(l), 0);
  const totalDeals = wonInWindow.length;
  const avgDealValue = totalDeals > 0 ? totalRevenue / totalDeals : 0;
  
  // Standard conversion rate: Won in period / Active leads in period
  const leadConvRate = leadsInWindow.length > 0 ? (totalDeals / leadsInWindow.length) * 100 : 0;

  // Real Trend Calculations
  const getPrevStartDate = () => {
    // For 'all' time, compare the last 30 days vs the 30 days before that
    if (timeRange === 'all') return subDays(subDays(now, 29), 30);
    
    const diffDays = Math.max(1, Math.round((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
    return subDays(startDate, diffDays);
  };
  
  const prevStartDate = getPrevStartDate();
  const prevEndDate = timeRange === 'all' ? subDays(now, 29) : startDate;
  
  // Previous Leads ACTIVE in prev window
  const prevLeads = (leads || []).filter(l => {
    const createdDate = new Date(l.createdAt);
    if (createdDate > prevEndDate) return false;
    
    const closeEntry = l.statusHistory?.find((h: any) => h.toStatus === 'closed-won' || h.toStatus === 'closed-lost');
    const closeDate = closeEntry ? new Date(closeEntry.timestamp) : (l.status.startsWith('closed') ? new Date(l.updatedAt) : null);
    
    if (closeDate && closeDate < prevStartDate) return false;
    return true;
  });
  
  const prevWon = (leads || []).filter(l => wasWonInPeriod(l, prevStartDate, prevEndDate));
  
  const prevRevenue = prevWon.reduce((sum, l) => sum + getDealValue(l), 0);
  const prevConvRate = prevLeads.length > 0 ? (prevWon.length / prevLeads.length) * 100 : 0;
  const prevAvgDeal = prevWon.length > 0 ? prevRevenue / prevWon.length : 0;

  const getTrendValue = (curr: number, prev: number) => {
    // User requested: If previous period truly has 0, show "N/A"
    // Also handling near-zero values to avoid ridiculous percentages
    if (!prev || prev < 0.01) return null;
    
    const diff = ((curr - prev) / prev) * 100;
    
    // Safety check for Infinity/NaN
    if (!isFinite(diff)) return null;

    return `${diff >= 0 ? '+' : ''}${diff.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  };

  const getPrevComparisonLabel = () => {
    if (timeRange === '7d') return 'vs last 7d';
    if (timeRange === '30d') return 'vs last month';
    if (timeRange === '90d') return 'vs last 90d';
    if (timeRange === 'all') return 'vs prev. month';
    return 'vs last period';
  };

  const compLabel = getPrevComparisonLabel();

  // Avg Close Time
  const closedLeads = wonInWindow;
  const avgCloseTime = closedLeads.length > 0 
    ? closedLeads.reduce((sum, l) => {
        const start = new Date(l.createdAt).getTime();
        const winEntry = l.statusHistory?.find((h: any) => h.toStatus === 'closed-won');
        const end = winEntry ? new Date(winEntry.timestamp).getTime() : new Date(l.updatedAt!).getTime();
        return sum + (end - start);
      }, 0) / (closedLeads.length * 1000 * 60 * 60 * 24)
    : 0;

  // Metric Toggles
  const [visibleMetrics, setVisibleMetrics] = useState({
    leads: true,
    deals: true,
    revenue: true,
    profit: false,
    conversion: false
  });

  // Performance Data Generation
  const days = eachDayOfInterval({
    start: startDate,
    end: now,
  });

  const dailyPerformance = days.map(date => {
    const dayLeads = leads.filter(l => isSameDay(new Date(l.createdAt), date));
    const dayDeals = leads.filter(l => wasWonInPeriod(l, startOfDay(date), new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)));
    const dayRevenue = dayDeals.reduce((sum, l) => sum + getDealValue(l), 0);
    return {
      name: format(date, timeRange === 'all' ? 'MMM yyyy' : 'MMM dd'),
      leads: dayLeads.length,
      deals: dayDeals.length,
      revenue: dayRevenue,
      conversion: dayLeads.length > 0 ? (dayDeals.length / dayLeads.length) * 100 : 0,
      profit: dayRevenue * 0.35 // Real margin estimated at 35% standard for platform services
    };
  });

  const memberPerformance = team.map(member => {
    const memberLeads = leadsInWindow.filter(l => l.assignedTo === member.id);
    const memberDeals = wonInWindow.filter(l => l.assignedTo === member.id);
    const memberRevenue = memberDeals.reduce((sum, l) => sum + getDealValue(l), 0);
    return {
      name: member.name,
      leads: memberLeads.length,
      deals: memberDeals.length,
      revenue: memberRevenue,
      conversion: memberLeads.length > 0 ? (memberDeals.length / memberLeads.length) * 100 : 0
    };
  }).sort((a, b) => b.revenue - a.revenue);

  const stageDistribution = [
    { name: 'New', value: leadsInWindow.filter(l => l.status === 'new').length },
    { name: 'Contacted', value: leadsInWindow.filter(l => l.status === 'contacted').length },
    { name: 'Qualified', value: leadsInWindow.filter(l => l.status === 'qualified').length },
    { name: 'Negotiating', value: leadsInWindow.filter(l => l.status === 'negotiating').length },
    { name: 'Won', value: wonInWindow.length },
    { name: 'Lost', value: leadsInWindow.filter(l => l.status === 'closed-lost').length },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Team Analytics Hub</h1>
          <p className="text-sm text-[var(--t-text-muted)]">Data-driven performance insights & pipeline metrics.</p>
        </div>

        <div className="flex items-center gap-3 p-1.5 rounded-2xl bg-[var(--t-surface)] border border-[var(--t-border)] shadow-sm">
          {(['7d', '30d', '90d', 'all'] as const).map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${timeRange === range ? 'bg-[var(--t-primary)] text-white shadow-lg' : 'text-[var(--t-text-muted)] hover:text-[var(--t-text)]'}`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard 
          title="Total Revenue" 
          value={totalRevenue} 
          change={getTrendValue(totalRevenue, prevRevenue)} 
          changeType={totalRevenue >= prevRevenue ? 'up' : 'down'} 
          icon={DollarSign} 
          formatter={formatCurrency} 
          changeLabel={compLabel}
        />
        <MetricCard 
          title="Total Deals" 
          value={totalDeals} 
          change={getTrendValue(totalDeals, prevWon.length)} 
          changeType={totalDeals >= prevWon.length ? 'up' : 'down'} 
          icon={Briefcase} 
          formatter={(v) => formatCompactNumber(Math.floor(v))} 
          changeLabel={compLabel}
        />
        <MetricCard 
          title="Avg Deal" 
          value={avgDealValue} 
          change={getTrendValue(avgDealValue, prevAvgDeal)} 
          changeType={avgDealValue >= prevAvgDeal ? 'up' : 'down'} 
          icon={Target} 
          formatter={formatCurrency} 
          changeLabel={compLabel}
        />
        <MetricCard 
          title="Conversion" 
          value={leadConvRate} 
          change={getTrendValue(leadConvRate, prevConvRate)} 
          changeType={leadConvRate >= prevConvRate ? 'up' : 'down'} 
          icon={Zap} 
          formatter={(v) => `${Math.round(v)}%`} 
          changeLabel={compLabel}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Revenue Performance Chart */}
        <div className="lg:col-span-2 rounded-[2.5rem] p-8 liquid-glass border border-[var(--t-border-subtle)] shadow-xl space-y-8">
           <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                 <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Performance Velocity</h3>
                 <p className="text-xs text-[var(--t-text-muted)]">Dynamic trend analysis across the team.</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                 {[
                   { key: 'revenue', label: 'Rev', color: 'var(--t-primary)' },
                   { key: 'profit', label: 'Profit', color: 'var(--t-success)' },
                   { key: 'deals', label: 'Deals', color: 'var(--t-accent)' },
                   { key: 'leads', label: 'Leads', color: 'var(--t-info)' },
                   { key: 'conversion', label: 'Conv', color: 'var(--t-warning)' },
                 ].map(({ key, label, color }) => (
                   <label key={key} className="flex items-center gap-2 cursor-pointer group">
                      <input 
                        type="checkbox" 
                        checked={visibleMetrics[key as keyof typeof visibleMetrics]} 
                        onChange={() => setVisibleMetrics(v => ({ ...v, [key]: !v[key as keyof typeof v] }))}
                        className="accent-[var(--t-primary)] scale-90"
                      />
                      <span className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)] group-hover:text-[var(--t-text)] transition-colors" style={{ color: visibleMetrics[key as keyof typeof visibleMetrics] ? color : undefined }}>
                        {label}
                      </span>
                   </label>
                 ))}
                 <div className="p-2 ml-2 rounded-xl bg-[var(--t-surface-dim)]">
                    <Activity size={18} className="text-[var(--t-primary)]" />
                 </div>
              </div>
           </div>

           <div className="h-[350px] w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                 <ComposedChart data={dailyPerformance}>
                    <defs>
                       <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--t-primary)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="var(--t-primary)" stopOpacity={0}/>
                       </linearGradient>
                       <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--t-success)" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="var(--t-success)" stopOpacity={0}/>
                       </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} opacity={0.1} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'var(--t-text-muted)', fontSize: 10, fontWeight: 'bold' }} />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: 'var(--t-text-muted)', fontSize: 10, fontWeight: 'bold' }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: 'var(--t-text-muted)', fontSize: 10, fontWeight: 'bold' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'var(--t-surface)', borderColor: 'var(--t-border)', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}
                      cursor={{ stroke: 'var(--t-primary)', strokeWidth: 1, strokeDasharray: '4 4' }}
                    />
                    {visibleMetrics.revenue && (
                      <Area yAxisId="left" type="monotone" dataKey="revenue" fill="url(#revGrad)" stroke="var(--t-primary)" strokeWidth={3} />
                    )}
                    {visibleMetrics.profit && (
                      <Area yAxisId="left" type="monotone" dataKey="profit" fill="url(#profitGrad)" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" />
                    )}
                    {visibleMetrics.leads && (
                      <Bar yAxisId="right" dataKey="leads" fill="#0ea5e9" opacity={0.4} radius={[4, 4, 0, 0]} barSize={20} />
                    )}
                    {visibleMetrics.deals && (
                      <Bar yAxisId="right" dataKey="deals" fill="var(--t-accent)" radius={[4, 4, 0, 0]} barSize={20} />
                    )}
                    {visibleMetrics.conversion && (
                      <Area yAxisId="right" type="monotone" dataKey="conversion" stroke="#f59e0b" strokeWidth={2} fill="transparent" />
                    )}
                 </ComposedChart>
              </ResponsiveContainer>
           </div>
        </div>

        {/* Pipeline Distribution */}
        <div className="rounded-[2.5rem] p-8 liquid-glass border border-[var(--t-border-subtle)] shadow-xl space-y-8">
           <div className="flex items-center justify-between">
              <div>
                 <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Pipeline Mix</h3>
                 <p className="text-xs text-[var(--t-text-muted)]">Lead status distribution.</p>
              </div>
              <Layers size={20} className="text-accent" />
           </div>

           <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                 <PieChart>
                    <Pie
                       data={stageDistribution}
                       cx="50%" cy="50%"
                       innerRadius={60} outerRadius={100}
                       paddingAngle={8}
                       dataKey="value"
                    >
                        {stageDistribution.map((_, index) => (
                           <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', fontWeight: 'bold', paddingTop: '20px' }} />
                 </PieChart>
              </ResponsiveContainer>
           </div>

           <div className="space-y-4">
              {stageDistribution.slice(0, 4).map((stage, i) => (
                 <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                       <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                       <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--t-text-muted)]">{stage.name}</span>
                    </div>
                    <span className="text-xs font-black text-[var(--t-text)]">{stage.value}</span>
                 </div>
              ))}
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Agent Performance List */}
        <div className="rounded-[2.5rem] p-8 liquid-glass border border-[var(--t-border-subtle)] shadow-xl space-y-8">
           <div className="flex items-center justify-between">
              <div>
                 <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Top Performers</h3>
                 <p className="text-xs text-[var(--t-text-muted)]">Individual contribution leaderboard.</p>
              </div>
              <TrendingUp size={20} className="text-emerald-500" />
           </div>

           <div className="space-y-4">
              {memberPerformance.map((member, i) => (
                 <div key={i} className="group flex items-center justify-between p-4 rounded-2xl bg-[var(--t-bg)] border border-[var(--t-border)] hover:border-[var(--t-primary)] transition-all">
                    <div className="flex items-center gap-4">
                       <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs bg-[var(--t-surface-dim)] text-[var(--t-primary)] group-hover:bg-[var(--t-primary)] group-hover:text-white transition-all">
                          #{i+1}
                       </div>
                       <div>
                          <p className="text-sm font-black text-[var(--t-text)]">{member.name}</p>
                          <p className="text-[10px] text-[var(--t-text-muted)] font-bold">{member.deals} Deals · {member.conversion.toFixed(1)}% Conv.</p>
                       </div>
                    </div>
                    <div className="text-right">
                       <p className="text-sm font-black text-emerald-500">${(member.revenue/1000).toFixed(1)}k</p>
                       <p className="text-[10px] text-[var(--t-text-muted)] font-bold uppercase">Revenue</p>
                    </div>
                 </div>
              ))}
           </div>
        </div>

        {/* Task Completion Analysis */}
        <div className="rounded-[2.5rem] p-8 liquid-glass border border-[var(--t-border-subtle)] shadow-xl space-y-8">
           <div className="flex items-center justify-between">
              <div>
                 <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Effort Metrics</h3>
                 <p className="text-xs text-[var(--t-text-muted)]">Team output & task completion rate.</p>
              </div>
              <CheckCircle2 size={20} className="text-blue-500" />
           </div>

           <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                 <BarChart data={dailyPerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} opacity={0.1} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'var(--t-text-muted)', fontSize: 10, fontWeight: 'bold' }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--t-text-muted)', fontSize: 10, fontWeight: 'bold' }} />
                    <Tooltip 
                       contentStyle={{ backgroundColor: 'var(--t-surface)', borderColor: 'var(--t-border)', borderRadius: '12px' }}
                    />
                    <Bar dataKey="leads" fill="var(--t-primary)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="deals" fill="var(--t-accent)" radius={[4, 4, 0, 0]} />
                 </BarChart>
              </ResponsiveContainer>
           </div>

           <div className="bg-[var(--t-bg)] p-6 rounded-2xl border border-[var(--t-border)]">
              <div className="flex items-center justify-between mb-4">
                 <div className="flex items-center gap-2">
                    <Clock size={16} className="text-[var(--t-primary)]" />
                    <span className="text-xs font-black uppercase tracking-widest">Avg. Close Time</span>
                 </div>
                 <span className="text-lg font-black text-[var(--t-text)]">{avgCloseTime.toFixed(1)} Days</span>
              </div>
              <div className="h-2 w-full bg-[var(--t-surface-dim)] rounded-full overflow-hidden">
                 <div className="h-full bg-[var(--t-primary)]" style={{ width: `${Math.min(100, (avgCloseTime / 14) * 100)}%` }} />
              </div>
           </div>
        </div>
      </div>
    </div>
  );
}

